/**
 * `<ClearPrefillLink>` — the "¿No eres tú?" affordance for forms that
 * read prefill from `useContactPrefill`. Pair this with the hook's
 * `source` (only show when `'localStorage'` — authed users can't
 * usefully clear their own data) and `clearAnonPrefill`.
 *
 * Intentionally tiny: text-link, no chrome. Forms that need a more
 * prominent affordance (toast, modal confirm) can build their own,
 * but in practice this lives below the form fields as a quiet escape
 * hatch and renders only when there's actually something to clear.
 *
 * Important: clearing the localStorage entry doesn't reset the form's
 * own field values — the caller must do that itself, typically via
 * react-hook-form's `reset()` or by calling `setValue` on each field.
 * The link's `onClear` callback should chain both: clear storage AND
 * reset form state.
 */

import { useTranslation } from 'next-i18next'

import type { ContactPrefillSource } from 'src/hooks/useContactPrefill'

type ClearPrefillLinkProps = {
  /**
   * The `source` returned by `useContactPrefill`. The link renders only
   * when this is `'localStorage'`. Authed prefill (`'authed'`) and no
   * prefill (`'none'`) hide the link entirely.
   */
  source: ContactPrefillSource
  /**
   * Click handler. Should chain `clearAnonPrefill()` from the hook with
   * the form's own reset (so the visible field values clear too).
   */
  onClear: () => void
  /**
   * Optional className override for surfaces that need different
   * alignment (right-aligned under a single field, centered under a
   * full-width form, etc.).
   */
  className?: string
}

export default function ClearPrefillLink({
  source,
  onClear,
  className
}: ClearPrefillLinkProps) {
  const { t } = useTranslation('common')

  if (source !== 'localStorage') return null

  return (
    <button
      type="button"
      onClick={onClear}
      className={
        className ??
        'self-start text-[12px] text-ink-200 underline-offset-4 hover:text-blue-500 hover:underline'
      }
    >
      {t('CONTACT_PREFILL.NOT_YOU')}
    </button>
  )
}
